import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { prop } from 'ramda';
import React from 'react';
import { Col, Row } from 'react-bootstrap';

import SelectField from 'Shared/form/SelectField';
import { TextField } from 'Shared/form/TextField';
import ToggleSwitchField from 'Shared/form/ToggleSwitchField';
import withFormikFields from 'Shared/form/withFormikFields';
import { profileOption, readLinkOption, readWriteLinkOption, writeLinkOption } from '../constants';
import useFetchQuestionLinkOptions from '../hooks/useFetchQuestionLinkOptions';
import questionLinkPropType from '../types/questionLinkPropType';
import questionPropType from '../types/questionPropType';

const QuestionLinkToggleRowStyle = styled(Row)`
    display: flex;

    .col-md-4 {
        z-index: 1;
    }
`;

const QuestionLinkDetailRowStyle = styled(Row)`
    margin-top: -2.5em;

    .col-md-4:first-of-type .control-label {
        visibility: hidden;
    }
`;

const QuestionLinkingField = ({ question, fieldValues }) => {
    const questionId = question.official_question_id || question.question_id;
    const questionLink = fieldValues[`questionLinks[${questionId}]`];
    const { data: linkOptions = {}, isLoading: isLinkOptionsLoading } = useFetchQuestionLinkOptions();

    if (question.type === 'message') {
        return null;
    }

    return (
        <>
            <QuestionLinkToggleRowStyle>
                <Col md={4}>
                    <ToggleSwitchField fieldName={`questionLinks.${questionId}.enabled`} label="Question Linking" />
                </Col>
            </QuestionLinkToggleRowStyle>

            {questionLink?.enabled && (
                <QuestionLinkDetailRowStyle>
                    <Col md={4}>
                        <SelectField
                            fieldName={`questionLinks.${questionId}.type`}
                            label="Question Linking Type"
                            options={[
                                readLinkOption,
                                writeLinkOption,
                                readWriteLinkOption,
                                ...(['textbox', 'textarea'].includes(question.type) ? [profileOption] : []),
                            ]}
                        />
                    </Col>
                    {[readLinkOption, readWriteLinkOption].includes(questionLink.type) && (
                        <Col md={4}>
                            <TextField
                                fieldName={`questionLinks.${questionId}.read_tag`}
                                label="Read Link"
                                placeholder="Read Link"
                                required
                                debounce
                            />
                        </Col>
                    )}
                    {[writeLinkOption, readWriteLinkOption].includes(questionLink.type) && (
                        <Col md={4}>
                            <TextField
                                fieldName={`questionLinks.${questionId}.write_tag`}
                                label="Write Link"
                                placeholder="Write Link"
                                required
                                debounce
                            />
                        </Col>
                    )}
                    {[profileOption].includes(questionLink.type) && (
                        <Col md={4}>
                            <SelectField
                                fieldName={`questionLinks.${questionId}.patient_table`}
                                label="Profile Field"
                                options={linkOptions[question.type] || []}
                                isLoading={isLinkOptionsLoading}
                                getOptionLabel={(o) =>
                                    linkOptions[question.type]?.find((link) => link.id === o.id)?.field_human_name
                                }
                                getOptionValue={prop('id')}
                            />
                        </Col>
                    )}
                </QuestionLinkDetailRowStyle>
            )}
        </>
    );
};

QuestionLinkingField.propTypes = {
    question: questionPropType.isRequired,
    fieldValues: PropTypes.objectOf(questionLinkPropType).isRequired,
};

export default withFormikFields({
    WrappedComponent: QuestionLinkingField,
    fieldNames: [(props) => `questionLinks[${props.question.official_question_id || props.question.question_id}]`],
});
